import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { GridContextProvider, GridDropZone, GridItem, swap } from 'react-grid-drag';
import classNames from 'classnames';
import DraggablePhoto from '../drag/DraggablePhoto';
import CommonDropzone from '../../common/components/CommonDropzone/CommonDropzone';
import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import PhotoGalleryGuideModal from './PhotoGalleryGuideModal';
import '../../assets/css/componentStyles/PhotoGalleryGuideModal.css';
import '../../assets/css/componentStyles/FormValidation.css';

// Helper to extract photo errors from backend error object
const getPhotoError = (validationErrors) =>
  validationErrors?.PHOTOS?.['vehicleMedia.photos'] || validationErrors?.PHOTOS || null;

function AddPhotoForm({
  form,
  onPhotoDrop,
  onPhotoDelete,
  onPhotoReorder,
  className,
  maxPhotos = 50,
  photosPerRow = 3,
  minPhotos = 15,
  acceptedFormats = 'image/*',
  dropzoneText = 'OR DRAG & DROP PHOTO HERE',
  validationErrors = {},
}) {
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);

  const photos = useMemo(
    () => form.values.vehicleMedia?.photos || [],
    [form.values.vehicleMedia?.photos],
  );
  const photoError = getPhotoError(validationErrors);

  const toggleGuideModal = useCallback(() => {
    setIsGuideModalOpen((prev) => !prev);
  }, []);

  const handleDragStop = useCallback(
    (_sourceId, sourceIndex, targetIndex) => {
      if (sourceIndex === targetIndex) return;
      const nextState = swap(photos, sourceIndex, targetIndex);
      onPhotoReorder(nextState);
    },
    [photos, onPhotoReorder],
  );

  const fieldsToShow = useMemo(() => {
    const actualPhotoCount = photos.length;

    if (actualPhotoCount === 0) {
      return photosPerRow;
    }

    const completeRows = Math.floor(actualPhotoCount / photosPerRow);

    const hasIncompleteRow = actualPhotoCount % photosPerRow !== 0;
    const rowsToShow = hasIncompleteRow ? completeRows + 1 : completeRows + 1;

    return Math.min(rowsToShow * photosPerRow, maxPhotos);
  }, [photos.length, photosPerRow, maxPhotos]);

  const handlePhotoDrop = useCallback(
    (index, files) => {
      const nextAvailableIndex = photos.length;
      onPhotoDrop(nextAvailableIndex, files);
    },
    [onPhotoDrop, photos.length],
  );

  const handlePhotoDelete = useCallback(
    (index) => {
      onPhotoDelete(index);
    },
    [onPhotoDelete],
  );

  const gridStyle = useMemo(() => {
    const rows = Math.ceil(fieldsToShow / photosPerRow);
    return {
      display: 'grid',
      gridTemplateColumns: `repeat(${photosPerRow}, 1fr)`,
      height: `${rows * 200}px`,
      marginTop: '20px',
    };
  }, [photosPerRow, fieldsToShow]);

  const wrapperClassName = classNames('draggable-item mt-5', className);
  return (
    <AddVehicleContentWrapper>
      <SettingsSectionHeader
        header="Photos"
        description={`Minimum of ${minPhotos} images is required. Drag & drop to reorder. You can also drop a file onto an empty tile`}
        helpButton={
          <button
            type="button"
            className="help-button"
            onClick={toggleGuideModal}
            title="Photo Gallery Guide"
            aria-label="Photo Gallery Guide"
            style={{ background: 'transparent', border: 'none', padding: 0 }}
          >
            ?
          </button>
        }
      />
      {photoError && photos.length < minPhotos && (
        <div className="field-error-message mt-2">{photoError}</div>
      )}

      {photos.length === 0 ? (
        <div className={wrapperClassName} role="region" aria-label="Photo upload area">
          <CommonDropzone
            onDrop={(files) => handlePhotoDrop(0, files)}
            accept={acceptedFormats}
            multiple={true}
            dropzoneText={dropzoneText}
            aria-label="Upload first photo"
          />
        </div>
      ) : (
        <GridContextProvider onChange={handleDragStop}>
          <GridDropZone
            id="photos"
            boxesPerRow={photosPerRow}
            rowHeight={200}
            style={gridStyle}
            className="photo-grid-dropzone"
            role="grid"
            aria-label="Photo gallery grid"
          >
            {photos.map((photo, index) => {
              const originalIndex = photos.findIndex((p) => p === photo);
              return (
                <GridItem key={photo.id || index} role="gridcell">
                  <div style={{ padding: 5, height: '100%', width: '100%' }}>
                    <DraggablePhoto
                      photo={photo}
                      onDelete={() => handlePhotoDelete(originalIndex)}
                      aria-label={`Photo ${index + 1} of ${photos.length}`}
                      style={{ height: '100%', width: '100%', borderRadius: '20px' }}
                    />
                  </div>
                </GridItem>
              );
            })}

            {Array.from({ length: fieldsToShow - photos.length }).map((_, index) => {
              const dropzoneIndex = photos.length + index;
              return (
                <GridItem key={`empty-${dropzoneIndex}`} role="gridcell">
                  <div style={{ padding: 5, height: '100%', width: '100%' }}>
                    <div style={{ height: '100%', width: '100%', borderRadius: '20px' }}>
                      <CommonDropzone
                        onDrop={(files) => handlePhotoDrop(dropzoneIndex, files)}
                        accept={acceptedFormats}
                        multiple={true}
                        dropzoneText={dropzoneText}
                        aria-label={`Upload photo ${dropzoneIndex + 1}`}
                      />
                    </div>
                  </div>
                </GridItem>
              );
            })}
          </GridDropZone>
        </GridContextProvider>
      )}

      <PhotoGalleryGuideModal isOpen={isGuideModalOpen} toggle={toggleGuideModal} />
    </AddVehicleContentWrapper>
  );
}

AddPhotoForm.propTypes = {
  form: PropTypes.shape({
    values: PropTypes.shape({
      vehicleMedia: PropTypes.shape({
        photos: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          }),
        ),
      }),
    }).isRequired,
  }).isRequired,
  onPhotoDrop: PropTypes.func.isRequired,
  onPhotoDelete: PropTypes.func.isRequired,
  onPhotoReorder: PropTypes.func.isRequired,
  className: PropTypes.string,
  maxPhotos: PropTypes.number,
  photosPerRow: PropTypes.number,
  minPhotos: PropTypes.number,
  acceptedFormats: PropTypes.string,
  dropzoneText: PropTypes.string,
  validationErrors: PropTypes.object,
};

AddPhotoForm.defaultProps = {
  className: '',
  maxPhotos: 50,
  photosPerRow: 3,
  minPhotos: 15,
  acceptedFormats: 'image/*',
  dropzoneText: 'OR DRAG & DROP PHOTO HERE',
  validationErrors: {},
};

export default AddPhotoForm;
